import { getOwnProfile } from 'actions/profile-actions'
import { getMySocialMatches } from 'actions/social-matches-actions'
import { getFeaturesConfig, logUserOut, startApp } from 'actions/user-actions'
import { getWatchlists, setDestinationWatchlistIds } from 'actions/watchlist-actions'
import { SOCIAL_MATCHES_POLLING_INTERVAL_IN_MS } from 'constants/social-matches'
import { DESTINATION_WATCHLIST_ID_PARAM } from 'constants/watchlists'
import Storage from 'modules/storage'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectOwnTennisId } from 'redux/selectors'
import { MatchUpSubmissionStateEnum } from 'types/api/enums'
import { UserStatus } from 'types/global'
import {
  emitSuccessfulRegistration,
  getAllQueryParams,
  isReferredByOther,
  removeParamFromHistory,
} from 'utils'

const defaulPagingArgs = { limit: 4, skip: 0 }

export const useAppInit = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    // In most cases the referrer is going to be different because of a login/logout redirect, in which case we want to clear token and user data
    if (isReferredByOther()) Storage.clearAllUserData()
    dispatch(getFeaturesConfig())
    dispatch(startApp())
  }, [dispatch])
}

export const useMyProfile = () => {
  const dispatch = useDispatch()
  const tennisId = useSelector(selectOwnTennisId)

  useEffect(() => {
    if (tennisId) dispatch(getOwnProfile())
  }, [dispatch, tennisId])
}

export const useLogOutUnauthenticated = ({
  isLoggedIn,
  userStatus,
}: {
  isLoggedIn: boolean
  userStatus: UserStatus | null
}) => {
  const dispatch = useDispatch()
  useEffect(() => {
    if (isLoggedIn && userStatus === UserStatus.UNAUTHENTICATED) dispatch(logUserOut())
  }, [userStatus, isLoggedIn, dispatch])
}

export const useMyWatchlists = ({
  isLoggedIn,
  userStatus,
}: {
  isLoggedIn: boolean
  userStatus: UserStatus | null
}) => {
  const dispatch = useDispatch()
  const { destinationWatchlistId } = useMemo(() => {
    const params = getAllQueryParams()
    const destinationWatchlistId = params[DESTINATION_WATCHLIST_ID_PARAM]
    if (destinationWatchlistId) removeParamFromHistory(DESTINATION_WATCHLIST_ID_PARAM)
    return {
      destinationWatchlistId,
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn && userStatus === UserStatus.ACTIVE) {
      dispatch(getWatchlists())
      const watchlistIds = destinationWatchlistId
        ? [destinationWatchlistId]
        : Storage.getDestinationWatchlistIds()
      if (watchlistIds) {
        dispatch(setDestinationWatchlistIds(watchlistIds))
      }
    }
  }, [userStatus, isLoggedIn, dispatch, destinationWatchlistId])
}

export const useSubscribeToSocialMatchUpdates = ({
  isLoggedIn,
  enableSelfSubmission,
  pageArgs = defaulPagingArgs,
  pollingInterval = SOCIAL_MATCHES_POLLING_INTERVAL_IN_MS,
}: {
  isLoggedIn: boolean
  enableSelfSubmission?: boolean
  pageArgs?: { limit: number; skip: number }
  pollingInterval?: number
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    let timer
    if (isLoggedIn && enableSelfSubmission) {
      dispatch(getMySocialMatches(MatchUpSubmissionStateEnum.APPROVED, pageArgs))
      timer = setInterval(() => {
        dispatch(getMySocialMatches(MatchUpSubmissionStateEnum.PENDING, pageArgs))
      }, pollingInterval)
    }

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [isLoggedIn, dispatch, enableSelfSubmission, pollingInterval, pageArgs])
}

export const useRegistrationEvent = () => {
  useEffect(() => {
    const regType = Storage.getRegistrationSeed()
    if (regType) {
      // trigger registration event to be picked up by analytics scripts
      emitSuccessfulRegistration(regType)
      console.log('emitted registration event')
      Storage.clearRegistrationSeed()
    }
  }, [])
}
